import React from 'react';
import { FiLock, FiEye, FiEyeOff, FiCheck, FiX } from 'react-icons/fi';
import logger from '../../utils/logging/logger';
import useSimplePasswordChangeModal from './useSimplePasswordChangeModal';
import {
  ModalOverlay, ModalContent, CloseButton, Title, Subtitle, Form, FormGroup, Label,
  InputWrapper, Icon, Input, PasswordToggle, Button, ErrorMessage, SuccessMessage,
  LoadingSpinner, RequirementsList, RequirementItem, RequirementIcon
} from './SimplePasswordChangeModal.styles';

// requireCurrentPassword=false is for a new student choosing a first password (they never had one).
function SimplePasswordChangeModal({ isOpen, onClose, onSuccess, requireCurrentPassword = true }) {
  const {
    formData, showPasswords, loading, error, success, passwordRequirements, isPasswordValid,
    passwordsMatch, isDifferentFromCurrent, handleChange, togglePasswordVisibility, handleSubmit
  } = useSimplePasswordChangeModal({ onSuccess, requireCurrentPassword });

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            logger.debug('Close button clicked', { onClose: typeof onClose });
            onClose();
          }}
          aria-label="Close modal"
        >
          <FiX size={20} />
        </CloseButton>

        <Title>{requireCurrentPassword ? 'Change Your Password' : 'Set Your Password'}</Title>
        <Subtitle>
          {requireCurrentPassword
            ? 'For security reasons, you must change your password before continuing.'
            : 'Choose a password to activate your account and access your courses.'}
        </Subtitle>

        <Form onSubmit={handleSubmit}>
          {requireCurrentPassword && (
          <FormGroup>
            <Label>Current Password</Label>
            <InputWrapper>
              <Icon>
                <FiLock size={16} />
              </Icon>
              <Input
                type={showPasswords.current ? 'text' : 'password'}
                name="currentPassword"
                placeholder="Enter current password"
                value={formData.currentPassword}
                onChange={handleChange}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                aria-label={showPasswords.current ? 'Hide password' : 'Show password'}
              >
                {showPasswords.current ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </PasswordToggle>
            </InputWrapper>
          </FormGroup>
          )}

          <FormGroup>
            <Label>New Password</Label>
            <InputWrapper>
              <Icon>
                <FiLock size={16} />
              </Icon>
              <Input
                type={showPasswords.new ? 'text' : 'password'}
                name="newPassword"
                placeholder="Enter new password"
                value={formData.newPassword}
                onChange={handleChange}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                aria-label={showPasswords.new ? 'Hide password' : 'Show password'}
              >
                {showPasswords.new ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </PasswordToggle>
            </InputWrapper>
            {/* a first-time password has no "current password" step, so the rules are shown up front */}
            {(formData.newPassword || !requireCurrentPassword) && (
              <RequirementsList>
                <RequirementItem valid={passwordRequirements.length}>
                  <RequirementIcon valid={passwordRequirements.length}>
                    {passwordRequirements.length ? '✓' : '•'}
                  </RequirementIcon>
                  At least 10 characters
                </RequirementItem>
                <RequirementItem valid={passwordRequirements.digit}>
                  <RequirementIcon valid={passwordRequirements.digit}>
                    {passwordRequirements.digit ? '✓' : '•'}
                  </RequirementIcon>
                  At least 1 digit
                </RequirementItem>
                <RequirementItem valid={passwordRequirements.specialChar}>
                  <RequirementIcon valid={passwordRequirements.specialChar}>
                    {passwordRequirements.specialChar ? '✓' : '•'}
                  </RequirementIcon>
                  At least 1 special character
                </RequirementItem>
                {requireCurrentPassword && (
                <RequirementItem valid={isDifferentFromCurrent}>
                  <RequirementIcon valid={isDifferentFromCurrent}>
                    {isDifferentFromCurrent ? '✓' : '•'}
                  </RequirementIcon>
                  Different from current password
                </RequirementItem>
                )}
              </RequirementsList>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Confirm New Password</Label>
            <InputWrapper>
              <Icon>
                <FiLock size={16} />
              </Icon>
              <Input
                type={showPasswords.confirm ? 'text' : 'password'}
                name="confirmPassword"
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                aria-label={showPasswords.confirm ? 'Hide password' : 'Show password'}
              >
                {showPasswords.confirm ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </PasswordToggle>
            </InputWrapper>
          </FormGroup>

          {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
          {success && <SuccessMessage role="alert">{success}</SuccessMessage>}

          <Button
            type="submit"
            disabled={loading || !isPasswordValid || !passwordsMatch || (requireCurrentPassword && !formData.currentPassword) || !isDifferentFromCurrent}
          >
            {loading ? <LoadingSpinner /> : <FiCheck size={16} />}
            {requireCurrentPassword
              ? (loading ? 'Changing Password...' : 'Change Password')
              : (loading ? 'Setting Password...' : 'Set Password')}
          </Button>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
}

export default SimplePasswordChangeModal;
